import { VendorType, ValidationGuideType } from '../types/types';

const INVALID_PORT_MESSAGE = 'forbidden port';

const MERAKI_MAC_ADDRESS_REGEX = /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/;
const MERAKI_PORT_NUMBER_REGEX = /^\d+$/;

const isValidVlan = (vendor: string, vlan: string) => {
  if (vendor === VendorType.meraki) return vlan === 'trunk' || vlan === 'stack';

  return vlan === 'trunk';
};

/**
 * Validates MAC address/port format (e.g., "00:7A:7B:7C:7D:7E/1")
 * Port must be a valid TCP/UDP port number between 1-65535
 * MAC address must use colon notation (e.g., "00:11:22:33:44:55")
 *
 * @param port - MAC address/port string to validate
 * @returns true if the format is valid, false otherwise
 */
export const isValidMerakiPort = (port: string): boolean => {
  if (!port.includes('/')) {
    return false;
  }

  const [macAddress, portNumber] = port.split('/');
  if (!macAddress || !portNumber) {
    return false;
  }

  if (!MERAKI_MAC_ADDRESS_REGEX.test(macAddress)) {
    return false;
  }

  if (!MERAKI_PORT_NUMBER_REGEX.test(portNumber)) {
    return false;
  }

  const num = parseInt(portNumber, 10);
  return num > 0 && num < 65536;
};

const isValidCiscoPort = (port: string): boolean => {
  return port.startsWith('Tw') || port.startsWith('Te') || port.startsWith('Gi');
};

const isValidJuniperPort = (port: string): boolean => {
  return port.startsWith('ge-') || port.startsWith('mge-');
};

const isValidBrocadeRuckusPort = (port: string): boolean => {
  if (port.startsWith('lg') || port.startsWith('mgmt')) {
    return false;
  }

  const formatRegex = /^[0-9]+\/1\/[0-9]+$/;
  return formatRegex.test(port);
};

export const validatePort = (vendor: string, entity: { vlan: string; port: string }): string[] => {
  const invalidReasons: string[] = [];

  if (isValidVlan(vendor, entity.vlan)) {
    invalidReasons.push(
      vendor !== VendorType.meraki ? 'VLAN can\'t be trunk' : 'VLAN can\'t be trunk or stack'
    );
  }

  switch (vendor) {
    case VendorType.cisco:
      if (!isValidCiscoPort(entity.port)) {
        invalidReasons.push(INVALID_PORT_MESSAGE);
      }
      break;
    case VendorType.juniper:
      if (!isValidJuniperPort(entity.port)) {
        invalidReasons.push(INVALID_PORT_MESSAGE);
      }
      break;
    case VendorType.meraki:
      if (!isValidMerakiPort(entity.port)) {
        invalidReasons.push(INVALID_PORT_MESSAGE);
      }
      break;
    case VendorType.brocade:
    case VendorType.ruckus:
      if (!isValidBrocadeRuckusPort(entity.port)) {
        invalidReasons.push(INVALID_PORT_MESSAGE);
      }
      break;
    default:
      invalidReasons.push('unsupported vendor');
      break;
  }

  return invalidReasons;
};

export const validationGuide: ValidationGuideType[] = [
  {
    vendor: 'juniper',
    prefixes: ['ge (e.g., ge-0/0/0, ge-0/0/11)', 'mge (e.g., mge-0/0/0, mge-0/0/11)'],
    vlan: '\'trunk\'',
    entity: '\'isp\' or \'distro\'',
  },
  {
    vendor: 'meraki',
    prefixes: ['MAC address (e.g., 00:7A:7B:7C:7D:7E/1)', 'Port number must be an integer'],
    vlan: '\'trunk\' or \'stack\'',
  },
  {
    vendor: 'cisco',
    prefixes: [
      'Tw (e.g., Tw1/1/1, Tw7/7/7)',
      'Te (e.g., Te1/1/1, Te1/7/7)',
      'Gi (e.g., Gi1/1/1, Gi7/7/7)',
    ],
    vlan: '\'trunk\'',
  },
  {
    vendor: 'brocade',
    prefixes: ['x/1/x (e.g., 1/1/1, 1/1/11)'],
    vlan: '\'trunk\'',
  },
  {
    vendor: 'ruckus',
    prefixes: ['x/1/x (e.g., 1/1/1, 1/1/11)'],
    vlan: '\'trunk\'',
  },
];
